"use client";

import { useI18n } from "@/lib/i18n";

import { useCallback, useEffect, useRef, useState } from "react";
import { signOut, useSession } from "next-auth/react";

const IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_BEFORE_TIMEOUT_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS = ["mousedown", "keydown", "scroll", "touchstart"];

function loginUrl(reason) {
  const next = `${window.location.pathname}${window.location.search}`;
  return `/login?reason=${encodeURIComponent(reason)}&next=${encodeURIComponent(next)}`;
}

export default function SessionTimeoutManager() {
  const { t } = useI18n();
  const { data: session, status } = useSession();
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const signingOutRef = useRef(false);

  const expireSession = useCallback(async (reason) => {
    if (signingOutRef.current || window.location.pathname === "/login") return;

    signingOutRef.current = true;
    await signOut({ redirect: true, callbackUrl: loginUrl(reason) });
  }, []);

  const registerActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setWarning(false);
  }, []);

  useEffect(() => {
    if (status !== "authenticated") return undefined;

    lastActivityRef.current = Date.now();
    ACTIVITY_EVENTS.forEach((event) => window.addEventListener(event, registerActivity));

    const timer = window.setInterval(() => {
      const idleFor = Date.now() - lastActivityRef.current;
      const remaining = IDLE_TIMEOUT_MS - idleFor;

      if (remaining <= 0) {
        window.clearInterval(timer);
        expireSession("timeout");
        return;
      }

      if (remaining <= WARNING_BEFORE_TIMEOUT_MS) {
        setWarning(true);
        setSecondsLeft(Math.ceil(remaining / 1000));
      }
    }, 1000);

    return () => {
      window.clearInterval(timer);
      ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, registerActivity));
    };
  }, [expireSession, registerActivity, status]);

  useEffect(() => {
    if (session?.error === "RefreshAccessTokenError") {
      expireSession("refresh-failed");
    }
  }, [expireSession, session?.error]);

  if (!warning || status !== "authenticated") return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = String(secondsLeft % 60).padStart(2, "0");

  return (
    <div className="fixed inset-x-0 top-4 z-50 flex justify-center px-4" role="alert">
      <div className="w-full max-w-xl rounded-xl border border-amber-500/60 bg-neutral-900 px-5 py-4 text-white shadow-2xl">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="font-semibold">{t("session.warning")}</p>
            <p className="text-sm text-neutral-300">
              {t("session.countdown", { time: `${minutes}:${seconds}` })}
            </p>
          </div>
          <button
            type="button"
            onClick={registerActivity}
            className="rounded-lg bg-amber-500 px-4 py-2 text-sm font-medium text-neutral-950 hover:bg-amber-400"
          >
            {t("session.continue")}
          </button>
        </div>
      </div>
    </div>
  );
}
